/**
 * Shared device-selection table for the EVAR report.
 *
 * Lists the devices added by the user (Part / Side / Reference), grouped under
 * a "manufacturer — model" header per catalog. Callers only render it when the
 * assembly has at least one part.
 */

import { Fragment, useMemo } from 'react';

import type { IAssemblyEntry, ICatalogDescriptor } from '@/workflows/vascular/common/catalog';
import { groupAndSortAssembly } from '@/workflows/vascular/common/catalog';

interface DeviceSelectionTableProps {
    assembly: readonly IAssemblyEntry[];
    catalogDescriptors: readonly ICatalogDescriptor[];
}

export function DeviceSelectionTable({ assembly, catalogDescriptors }: DeviceSelectionTableProps) {
    // Group added parts by catalogId, sorted within each group by anatomical order.
    const groupedEntries = useMemo(() => groupAndSortAssembly(assembly), [assembly]);
    const catalogIds = [...groupedEntries.keys()];

    const getCatalogDescriptor = (catalogId: string) => catalogDescriptors.find((d) => d.id === catalogId);

    return (
        <table className="w-full text-[10px]">
            <thead>
                <tr className="border-b border-gray-300">
                    <th className="text-left py-1 pr-2 font-semibold text-gray-600">Part</th>
                    <th className="text-left py-1 pr-2 font-semibold text-gray-600">Side</th>
                    <th className="text-left py-1 pr-2 font-semibold text-gray-600">Reference</th>
                </tr>
            </thead>
            <tbody>
                {catalogIds.map((catalogId) => {
                    const desc = getCatalogDescriptor(catalogId);
                    const label = desc ? `${desc.manufacturer} — ${desc.model}` : catalogId;
                    const entries = groupedEntries.get(catalogId) ?? [];
                    return (
                        <Fragment key={catalogId}>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <td colSpan={3} className="py-1 px-1 text-[10px] font-semibold text-gray-600">
                                    <span className="flex items-center gap-2">
                                        {desc?.logo && (
                                            <img
                                                src={desc.logo}
                                                alt={desc.manufacturer}
                                                className="h-4 object-contain"
                                            />
                                        )}
                                        {label}
                                    </span>
                                </td>
                            </tr>
                            {entries.map((entry) => (
                                <tr key={entry.instanceId} className="border-b border-gray-100">
                                    <td className="py-1 pr-2 font-mono text-gray-700">{entry.partLabel}</td>
                                    <td className="py-1 pr-2 text-gray-500">
                                        {entry.side !== 'none' ? (entry.side === 'left' ? 'L' : 'R') : '—'}
                                    </td>
                                    <td className="py-1 pr-2 font-mono font-semibold text-gray-800">
                                        {entry.resolvedRef ?? '—'}
                                    </td>
                                </tr>
                            ))}
                        </Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}
